import * as React from 'react';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';
import { FormSectionBase, FormSectionBaseState } from '../Types/FormSectionBase';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import { ResponseError } from 'sos-common/js/utils/ResponseError';
import { Fileupload } from 'sos-common/js/components/Fileupload/Fileupload';
import { FormFieldFile } from 'sos-common/js/commonTypes/formField.types';
import { FileExtendedDetails } from 'sos-common/js/commonTypes/fileupload.types';
import { ApiService } from '../Services/intyg.api.service';
import { PostFilesServerResponseObject, IAppContext, ValidationError } from '../Types/Intyg.types';

export interface IFileUploaderProps {
  maxIndividualFileSizeInBytes?: number;
}
export interface IFileUploaderState extends FormSectionBaseState {
  totalAttachedDocuments: Map<string, number>;
  fileUploadState: FileExtendedDetails[];
  serverResponseObject: PostFilesServerResponseObject;
}

/**
 * Includes one file upload component: examensbevis
 */
export default class FileUploader extends FormSectionBase<IFileUploaderProps, IFileUploaderState> {
  private _requiredErrorState: ValidationError;

  static defaultProps = {
    maxIndividualFileSizeInBytes: 5242880,
  };

  constructor(props: IFileUploaderProps, context: IAppContext) {
    super(props);

    this._requiredErrorState = this.getValidationError(context.Translate('error_mandatory'), false);

    const errors = new Map(this.emptyErrorsMap);
    if (context.model.bilagor?.length < 1) {
      errors.set('FILE_REQUIRED', this._requiredErrorState);
    }

    this.state = {
      errors: errors,
      fileUploadState: context.model.bilagor || [],
      serverResponseObject: null,
      totalAttachedDocuments: new Map<string, number>(),
    };
  }

  getValidationError = (errorMessage: string, showMessage: boolean = true): ValidationError => {
    return {
      showMessage,
      errorMessage: errorMessage || 'Ett ohanterat fel inträffade.',
    };
  };

  onAddFile = (field: FormFieldFile) => {
    const { ansokanId, updateModelUsingKeyValue } = this.context;

    this.setState({
      fileUploadState: field.value,
    });

    ApiService.call.addBilaga(ansokanId, field).then((response) => {
      const errors = new Map(this.state.errors);
      if (response instanceof ResponseError) {
        errors.set('FILE_ERROR', this.getValidationError(response.body.errorMessage));
        const files = field.value.filter((file) => !!file.FileId);
        this.setState({ fileUploadState: files, errors }, () => {
          updateModelUsingKeyValue('bilagor', this.context.model.bilagor);
        });
      } else {
        errors.delete('FILE_ERROR');
        errors.delete('FILE_REQUIRED');
        this.setState(
          {
            fileUploadState: response.fileUploadValue,
            serverResponseObject: response.serverResponse,
            errors,
          },
          () => {
            updateModelUsingKeyValue('bilagor', this.context.model.bilagor);
          }
        );
      }
    });
  };

  onDeleteFile = (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => {
    const { ansokanId, updateModelUsingKeyValue } = this.context;

    ApiService.call.deleteBilaga(ansokanId, file.FileId).then((response) => {
      const errors = new Map(this.state.errors);
      if (response instanceof ResponseError) {
        errors.set('FILE_ERROR', this.getValidationError(response.body.errorMessage));
        this.setState({ errors }, () => {
          updateModelUsingKeyValue('bilagor', this.context.model.bilagor);
        });
      } else {
        const fileId = response.FileId;
        let newFileUploadState = this.state.fileUploadState.slice();
        let index = newFileUploadState.findIndex((val) => {
          return val.FileId === fileId;
        });
        newFileUploadState.splice(index, 1);

        const errors = new Map(this.state.errors);
        if (newFileUploadState.length < 1) {
          errors.set('FILE_REQUIRED', this.getValidationError(this.context.Translate('error_mandatory'), true));
        }

        this.setState(
          {
            fileUploadState: newFileUploadState,
            errors,
          },
          () => {
            updateModelUsingKeyValue('bilagor', this.context.model.bilagor);
          }
        );
      }
    });
  };

  public render() {
    const { isInEditMode, Translate } = this.context;

    const fileError = this.getErrorMessage('FILE_ERROR') || this.getErrorMessage('FILE_REQUIRED');

    return (
      <section>
        <EpiserverProperty propertyName="DokumentHeading" isInEditMode={isInEditMode}>
          <h2>{this.findProperty('DokumentHeading')}</h2>
        </EpiserverProperty>

        <EpiserverProperty propertyName="DokumentPreamble" wrappingElement="span" isInEditMode={isInEditMode}>
          <p className="preamble">{this.findProperty('DokumentPreamble')}</p>
        </EpiserverProperty>

        <EpiserverProperty propertyName="DokumentBody" usingDangerouslySetInnerHTML={true} isInEditMode={isInEditMode}>
          <div className="m-t-1" dangerouslySetInnerHTML={setInnerHtml(this.findProperty('DokumentBody'))} />
        </EpiserverProperty>

        <div className="m-t-2">
          <Fileupload
            heading={Translate('doc_tjanstgoringsintyg')}
            mandatory={true}
            errorMessage={fileError}
            disabled={false}
            onAdd={this.onAddFile}
            onRemove={this.onDeleteFile}
            value={this.state.fileUploadState}
            max={10}
            maxFileSize={this.props.maxIndividualFileSizeInBytes}
            maxFileSizeErrorMessage={Translate('maxstorlek')}
            mimetype={INTYG_FILE_MIMETYPES}
            infoBubble={{ text: this.findProperty('DokumentInforuta'), isInEditMode, propertyName: 'DokumentInforuta' }}
            upload={Translate('laddaupp')}
            nofilesText={Translate('ingafiler')}
          />
        </div>
      </section>
    );
  }
}

export const INTYG_FILE_MIMETYPES = 'application/pdf,image/jpeg,image/jpg';
